// src/services/tutorialService.js - Professional Welcome Tutorial for Strategy Consultants
// Modular service following separation of concerns principles

import { auth } from '../firebaseConfig.js';

export default class TutorialService {
  constructor() {
    this.tutorialKey = 'pptHeroTutorialCompleted';
    this.featureFlagKey = 'pptHeroEnableTutorial';
    this.currentPanel = 0;
    this.totalPanels = 5;
    this.tutorialContainer = null;
    this.animationDuration = 300;
    
    // Initialize feature flag (enabled by default for new feature)
    if (localStorage.getItem(this.featureFlagKey) === null) {
      localStorage.setItem(this.featureFlagKey, 'true');
    }
    
    console.log('📚 TutorialService initialized');
  }

  // Check if tutorial should be shown (feature flag + completion status)
  shouldShowTutorial(userId = null) {
    const featureEnabled = localStorage.getItem(this.featureFlagKey) === 'true';
    if (!featureEnabled) {
      console.log('📚 Tutorial disabled by feature flag');
      return false;
    }

    // User-specific completion tracking
    const completionKey = userId ? `${this.tutorialKey}_${userId}` : this.tutorialKey;
    const completed = localStorage.getItem(completionKey) === 'true';
    
    console.log(`📚 Tutorial check - Enabled: ${featureEnabled}, Completed: ${completed}`);
    return !completed;
  }

  // Mark tutorial as completed for specific user
  markTutorialCompleted(userId = null) {
    const completionKey = userId ? `${this.tutorialKey}_${userId}` : this.tutorialKey;
    localStorage.setItem(completionKey, 'true');
    console.log('📚 Tutorial marked as completed for user:', userId || 'anonymous');
  }

  // Initialize tutorial for new users (called from PPT Hero scenes)
  async initializeTutorial(scene, firmAffiliation = 'Generic User') {
    try {
      console.log('📚 Initializing welcome tutorial for:', firmAffiliation);
      
      // Get user context for personalization
      const user = auth.currentUser;
      const userId = user ? user.uid : null;
      
      // CRITICAL: Only show tutorial once for verified users
      if (!user || !user.emailVerified) {
        console.log('📚 Tutorial skipped - user not verified');
        return false;
      }
      
      // Check if tutorial should be shown
      if (!this.shouldShowTutorial(userId)) {
        console.log('📚 Tutorial already completed for verified user, skipping');
        return false;
      }

      // Store scene reference for integration
      this.scene = scene;
      this.firmAffiliation = firmAffiliation;
      
      // Generate tutorial content
      this.panels = this.generateTutorialPanels(firmAffiliation);
      
      // Create and show tutorial
      this.createTutorialOverlay();
      this.showPanel(0);
      
      console.log('📚 Tutorial initialized successfully');
      return true;
      
    } catch (error) {
      console.error('Error initializing tutorial:', error);
      return false;
    }
  }

  // Generate consultant-focused tutorial panels
  generateTutorialPanels(firmAffiliation) {
    const isBainUser = firmAffiliation.includes('Bain');
    const firmName = firmAffiliation.split(' ')[0];
    
    const panels = [
      // Panel 1: Value Proposition (Hook consultants immediately)
      {
        id: 'value-prop',
        title: '🏆 PowerPoint Mastery for Strategy Consultants',
        subtitle: 'Measure efficiency • Compete with peers • Track improvement',
        content: 'In 5 minutes, see exactly how your PowerPoint skills compare to 160+ strategy consultants and identify shortcuts that save hours per presentation.',
        visual: 'performance-preview',
        buttonText: 'Show me how →',
        timeEstimate: '15 seconds'
      },
      
      // Panel 2: Quick Start (Immediate action)
      {
        id: 'quick-start', 
        title: '🎯 Get Started in 60 Seconds',
        subtitle: 'Simple 4-step process to see your PowerPoint rating',
        content: '1. Select round type → 2. Press START → 3. Complete PowerPoint challenges → 4. See your rating among professionals',
        visual: 'gameplay-preview',
        buttonText: 'What can I achieve? →',
        timeEstimate: '20 seconds'
      },
      
      // Panel 3: Performance Rating (Core value)
      {
        id: 'performance-rating',
        title: '📊 Track Your PowerPoint Excellence', 
        subtitle: '5-tier professional classification system',
        content: 'See exactly where you rank: Apprentice → Analyst → Associate → Manager → PPT Hero. Compare with 160+ verified professionals.',
        visual: 'category-progression',
        buttonText: isBainUser ? 'Show firm features →' : 'Let\'s begin →',
        timeEstimate: '25 seconds'
      }
    ];
    
    // Panel 4: Firm Benefits (Only for verified firm users)
    if (isBainUser) {
      panels.push({
        id: 'firm-benefits',
        title: `🏢 ${firmName} & Company Exclusive Features`,
        subtitle: 'Professional consulting-specific PowerPoint training',
        content: `${firmName}-specific shortcuts • Firm leaderboards • 15% advancement bonus • Professional branding and consulting-focused challenges`,
        visual: 'firm-preview',
        buttonText: 'Let\'s begin →',
        timeEstimate: '20 seconds'
      });
    }
    
    // Panel 5: Launch (Clear next step)
    panels.push({
      id: 'launch',
      title: '🚀 Ready to Begin Your PowerPoint Journey',
      subtitle: 'Start with professional baseline measurement',
      content: 'Begin with Calibration Round to establish your baseline skill level, then track your advancement to PPT Hero status.',
      visual: 'calibration-highlight',
      buttonText: 'START CALIBRATION →',
      timeEstimate: '10 seconds',
      isLaunchPanel: true
    });
    
    this.totalPanels = panels.length;
    return panels;
  }

  // Create professional tutorial overlay with animations
  createTutorialOverlay() {
    // Remove existing tutorial if present
    this.destroyTutorial();

    // Create main overlay container
    this.tutorialContainer = document.createElement('div');
    this.tutorialContainer.id = 'ppt-hero-tutorial-overlay';
    this.tutorialContainer.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: linear-gradient(135deg, rgba(45, 55, 72, 0.95), rgba(74, 85, 104, 0.95));
      z-index: 15000;
      display: flex;
      justify-content: center;
      align-items: center;
      font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
      backdrop-filter: blur(10px);
      opacity: 0;
      transition: opacity 0.4s ease;
    `;

    // Create tutorial panel container
    this.panelContainer = document.createElement('div');
    this.panelContainer.style.cssText = `
      background: white;
      border-radius: 20px;
      box-shadow: 0 25px 50px rgba(0, 0, 0, 0.3);
      max-width: 600px;
      width: 90%;
      max-height: 80vh;
      overflow: hidden;
      border: 2px solid #00d4ff;
      position: relative;
    `;

    // Add tutorial styles
    this.addTutorialStyles();
    
    // Add to DOM
    this.tutorialContainer.appendChild(this.panelContainer);
    document.body.appendChild(this.tutorialContainer);
    
    // Smooth fade-in
    requestAnimationFrame(() => {
      this.tutorialContainer.style.opacity = '1';
    });
  }

  // Add professional CSS styles for tutorial
  addTutorialStyles() {
    if (document.getElementById('tutorial-styles')) return;
    
    const style = document.createElement('style');
    style.id = 'tutorial-styles';
    style.textContent = `
      @keyframes slideInRight {
        from { transform: translateX(30px); opacity: 0; }
        to { transform: translateX(0); opacity: 1; }
      }
      
      @keyframes slideOutLeft {
        from { transform: translateX(0); opacity: 1; }
        to { transform: translateX(-30px); opacity: 0; }
      }
      
      @keyframes fadeIn {
        from { opacity: 0; transform: scale(0.95); }
        to { opacity: 1; transform: scale(1); }
      }
      
      .tutorial-panel {
        padding: 32px;
        text-align: center;
        animation: slideInRight 0.3s ease-out;
      }
      
      .tutorial-visual {
        width: 100%;
        height: 120px;
        background: linear-gradient(135deg, #f0f9ff, #e0f2fe);
        border: 2px solid #00d4ff;
        border-radius: 12px;
        display: flex;
        align-items: center;
        justify-content: center;
        margin: 20px 0;
        font-size: 48px;
        color: #00d4ff;
        animation: fadeIn 0.5s ease-out 0.2s both;
      }
      
      .tutorial-button {
        background: linear-gradient(135deg, #00d4ff, #0ea5e9);
        color: white;
        border: none;
        padding: 12px 24px;
        border-radius: 25px;
        font-size: 14px;
        font-weight: 600;
        cursor: pointer;
        transition: all 0.2s ease;
        margin-top: 20px;
      }
      
      .tutorial-button:hover {
        transform: translateY(-2px);
        box-shadow: 0 8px 20px rgba(0, 212, 255, 0.3);
      }
      
      .tutorial-progress {
        display: flex;
        justify-content: center;
        gap: 8px;
        margin-bottom: 20px;
      }
      
      .tutorial-progress-dot {
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background: #e2e8f0;
        transition: all 0.3s ease;
      }
      
      .tutorial-progress-dot.active {
        background: #00d4ff;
        transform: scale(1.2);
      }
      
      .tutorial-skip {
        position: absolute;
        top: 16px;
        right: 60px;
        background: none;
        border: none;
        color: #6b7280;
        font-size: 12px;
        cursor: pointer;
        text-decoration: underline;
      }
      
      .tutorial-skip:hover {
        color: #374151;
      }
      
      .tutorial-close {
        position: absolute;
        top: 16px;
        right: 16px;
        background: #f8fafc;
        border: 2px solid #e2e8f0;
        border-radius: 50%;
        width: 32px;
        height: 32px;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        color: #6b7280;
        font-weight: bold;
        transition: all 0.2s ease;
      }
      
      .tutorial-close:hover {
        background: #e2e8f0;
        color: #374151;
      }
    `;
    
    document.head.appendChild(style);
  }

  // Show specific tutorial panel with animations
  showPanel(panelIndex) {
    if (panelIndex >= this.panels.length || panelIndex < 0) return;
    
    this.currentPanel = panelIndex;
    const panel = this.panels[panelIndex];
    
    // Create panel HTML
    const panelHTML = this.createPanelHTML(panel, panelIndex);
    
    // Update container with smooth transition
    if (this.panelContainer && this.panelContainer.firstChild && this.panelContainer.firstChild.style) {
      this.panelContainer.firstChild.style.animation = 'slideOutLeft 0.2s ease-in';
      setTimeout(() => {
        if (this.panelContainer) {
          this.panelContainer.innerHTML = panelHTML;
          this.attachPanelEventListeners(panel);
        }
      }, 200);
    } else {
      if (this.panelContainer) {
        this.panelContainer.innerHTML = panelHTML;
        this.attachPanelEventListeners(panel);
      }
    }
  }

  // Create HTML for tutorial panel
  createPanelHTML(panel, panelIndex) {
    const visual = this.getVisualForPanel(panel.visual);
    const progressDots = Array.from({ length: this.totalPanels }, (_, i) => 
      `<div class="tutorial-progress-dot ${i === panelIndex ? 'active' : ''}"></div>`
    ).join('');

    return `
      <div class="tutorial-panel">
        <!-- Progress Indicator -->
        <div class="tutorial-progress">${progressDots}</div>
        
        <!-- Skip Button (not close) -->
        <button class="tutorial-skip">
          Skip tutorial
        </button>
        
        <!-- Close Button -->
        <button class="tutorial-close">
          ×
        </button>
        
        <!-- Panel Content -->
        <h2 style="font-size: 24px; font-weight: 600; color: #1f2937; margin: 0 0 8px 0;">${panel.title}</h2>
        <p style="font-size: 16px; color: #00d4ff; font-weight: 500; margin: 0 0 16px 0;">${panel.subtitle}</p>
        
        <!-- Visual -->
        <div class="tutorial-visual">
          ${visual}
        </div>
        
        <!-- Content -->
        <p style="font-size: 14px; color: #4b5563; line-height: 1.6; margin: 0 0 20px 0;">${panel.content}</p>
        
        <!-- Time Estimate -->
        <div style="font-size: 12px; color: #9ca3af; margin-bottom: 20px;">
          ⏱️ ${panel.timeEstimate} | Panel ${panelIndex + 1} of ${this.totalPanels}
        </div>
        
        <!-- Action Button -->
        <button class="tutorial-button">
          ${panel.buttonText}
        </button>
      </div>
    `;
  }

  // Get visual content for each panel type  
  getVisualForPanel(visualType) {
    const visuals = {
      'performance-preview': '📊',
      'gameplay-preview': '🎮', 
      'category-progression': '🏆',
      'firm-preview': '🏢',
      'calibration-highlight': '📚'
    };
    
    return visuals[visualType] || '🎯';
  }

  // Attach event listeners to current panel
  attachPanelEventListeners(panel) {
    // Make tutorialService globally accessible for button clicks
    window.tutorialService = this;
    
    // Set up button click handlers directly (safer than onclick attributes)
    const nextButton = this.panelContainer?.querySelector('.tutorial-button');
    if (nextButton) {
      nextButton.addEventListener('click', () => {
        this.nextPanel(panel.isLaunchPanel || false);
      });
    }
    
    const skipButton = this.panelContainer?.querySelector('.tutorial-skip');
    if (skipButton) {
      skipButton.addEventListener('click', () => {
        this.skipTutorial();
      });
    }
    
    const closeButton = this.panelContainer?.querySelector('.tutorial-close');
    if (closeButton) {
      closeButton.addEventListener('click', () => {
        this.closeTutorial();
      });
    }
    
    // Add keyboard navigation
    this.keydownHandler = (event) => {
      if (event.key === 'Escape') {
        this.closeTutorial();
      } else if (event.key === 'ArrowRight' || event.key === 'Space') {
        event.preventDefault();
        this.nextPanel(panel.isLaunchPanel || false);
      } else if (event.key === 'ArrowLeft' && this.currentPanel > 0) {
        event.preventDefault();
        this.showPanel(this.currentPanel - 1);
      }
    };
    
    document.addEventListener('keydown', this.keydownHandler);
  }

  // Navigate to next panel or complete tutorial
  nextPanel(isLaunchPanel = false) {
    try {
      console.log(`📚 Next panel called, current: ${this.currentPanel}, launch: ${isLaunchPanel}`);
      
      if (isLaunchPanel) {
        // Launch panel - complete tutorial and start calibration
        this.completeTutorial(true);
      } else if (this.currentPanel < this.panels.length - 1) {
        // Show next panel
        this.showPanel(this.currentPanel + 1);
      } else {
        // Final panel - complete tutorial
        this.completeTutorial(false);
      }
    } catch (error) {
      console.error('Error in nextPanel:', error);
      // Fallback - just close tutorial gracefully
      this.closeTutorial();
    }
  }

  // Complete tutorial and optionally launch calibration
  completeTutorial(shouldLaunchCalibration = false) {
    console.log('📚 Completing tutorial, launch calibration:', shouldLaunchCalibration);
    
    // Mark as completed
    const user = auth.currentUser;
    this.markTutorialCompleted(user ? user.uid : null);
    
    // Destroy tutorial with animation
    this.destroyTutorial();
    
    // Launch calibration if requested
    if (shouldLaunchCalibration && this.scene) {
      setTimeout(() => {
        this.launchCalibrationRound();
      }, 400); // Wait for tutorial to fully close
    }
    
    console.log('📚 Tutorial completed successfully');
  }

  // Launch calibration round directly from tutorial
  launchCalibrationRound() {
    try {
      console.log('📚 Tutorial launching Calibration Round...');
      
      // Use the scene's existing round selection system
      if (this.scene.rexUIManager && this.scene.rexUIManager.handleRoundSelection) {
        // Find calibration round data
        const calibrationRound = { 
          id: 'calibration', 
          name: 'Calibration Round',
          levels: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10]
        };
        
        // Select the round
        this.scene.rexUIManager.handleRoundSelection(calibrationRound, 1);
        
        // Highlight START button with notification
        setTimeout(() => {
          if (this.scene.showCenterNotification) {
            this.scene.showCenterNotification('Press START to begin your PowerPoint assessment!', 'info', 4000);
          }
        }, 1000);
        
        console.log('📚 Tutorial successfully launched Calibration Round');
      } else {
        console.warn('📚 Cannot launch Calibration - scene not available');
      }
      
    } catch (error) {
      console.error('Error launching calibration from tutorial:', error);
    }
  }

  // Skip tutorial entirely (for experienced users)
  skipTutorial() {
    console.log('📚 Tutorial skipped by user');
    this.completeTutorial(false);
  }

  // Close tutorial without marking as completed (can show again)
  closeTutorial() {
    console.log('📚 Tutorial closed by user');
    this.destroyTutorial();
  }

  // Manually show tutorial (for ribbon button)
  showTutorial(scene = null, firmAffiliation = 'Generic User') {
    console.log('📚 Manually showing tutorial');
    
    // Don't check completion status for manual show
    this.scene = scene;
    this.firmAffiliation = firmAffiliation;
    this.panels = this.generateTutorialPanels(firmAffiliation);
    this.createTutorialOverlay();
    this.showPanel(0);
  }

  // Clean up tutorial and event listeners
  destroyTutorial() {
    // Remove keyboard event listeners
    if (this.keydownHandler) {
      document.removeEventListener('keydown', this.keydownHandler);
      this.keydownHandler = null;
    }
    
    // Remove button event listeners
    if (this.panelContainer) {
      const buttons = this.panelContainer.querySelectorAll('button');
      buttons.forEach(button => {
        // Clone and replace to remove all event listeners
        const newButton = button.cloneNode(true);
        button.parentNode?.replaceChild(newButton, button);
      });
    }
    
    // Remove global reference
    if (window.tutorialService === this) {
      window.tutorialService = null;
    }
    
    // Remove DOM elements with animation
    if (this.tutorialContainer) {
      this.tutorialContainer.style.opacity = '0';
      setTimeout(() => {
        if (this.tutorialContainer && this.tutorialContainer.parentNode) {
          this.tutorialContainer.parentNode.removeChild(this.tutorialContainer);
        }
        this.tutorialContainer = null;
        this.panelContainer = null;
      }, 300);
    }
  }

  // Feature flag management methods
  static enableTutorial() {
    localStorage.setItem('pptHeroEnableTutorial', 'true');
    console.log('📚 Welcome tutorial ENABLED');
    console.log('💡 New users will see onboarding tutorial on first PPT Hero visit');
  }

  static disableTutorial() {
    localStorage.setItem('pptHeroEnableTutorial', 'false');
    console.log('📚 Welcome tutorial DISABLED');
    console.log('💡 New users will skip tutorial and go directly to round selection');
  }

  static checkTutorialStatus() {
    const enabled = localStorage.getItem('pptHeroEnableTutorial') === 'true';
    console.log(`📚 Tutorial Status: ${enabled ? 'ENABLED' : 'DISABLED'}`);
    console.log(`💡 To toggle: Use TutorialService.${enabled ? 'disableTutorial()' : 'enableTutorial()'}`);
    return enabled;
  }

  // Reset tutorial completion for testing
  static resetTutorialForUser(userId = null) {
    const completionKey = userId ? `pptHeroTutorialCompleted_${userId}` : 'pptHeroTutorialCompleted';
    localStorage.removeItem(completionKey);
    console.log('📚 Tutorial completion reset for user:', userId || 'current');
    console.log('💡 Tutorial will show again on next PPT Hero visit');
  }

  // Reset tutorial for all users (testing method)
  static resetTutorialGlobally() {
    // Remove all tutorial completion keys
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith('pptHeroTutorialCompleted')) {
        localStorage.removeItem(key);
      }
    });
    console.log('📚 Tutorial completion reset for ALL users');
    console.log('💡 All users will see tutorial again on next visit');
  }
}
